import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useNavigate, useParams } from "react-router-dom";
import api, { API_ORIGIN } from "../services/api";
import logo from "../assets/MaAz_Logo.png";

function CoursePlayer() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [modules, setModules] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [openModule, setOpenModule] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);

  // Lesson completion
  const [completedLessonIds, setCompletedLessonIds] = useState([]);
  const [markingLessonId, setMarkingLessonId] = useState(null);

  const videoRef = useRef(null);

  // Prevent multiple API requests when video remains above 90%
  const completionRequestRef = useRef(false);

  /* ================================================= */
  /* FETCH COURSE CONTENT AND PROGRESS                  */
  /* ================================================= */

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [contentResponse, progressResponse] = await Promise.all([
        api.get(`/students/course/${id}/full-content`),
        api.get(`/students/course/${id}/progress`)
      ]);

      const courseModules =
        contentResponse?.data?.modules || [];

      const progressData =
        progressResponse?.data?.data || {};

      setModules(courseModules);

      setCompletedLessonIds(
        progressData.completed_lesson_ids || []
      );

      if (courseModules.length > 0) {
        setOpenModule(courseModules[0].module_id);

        // Do not automatically play the first lesson
        setCurrentVideo(null);
        setCurrentLesson(null);
      }
    } catch (error) {
      console.error("Course content load error:", error);

      alert(
        error?.response?.data?.message ||
          "Unable to load course content"
      );
    }
  };

  /* ================================================= */
  /* HLS AND MP4 PLAYER                                 */
  /* ================================================= */

  useEffect(() => {
    if (!currentVideo || !videoRef.current) return;

    const video = videoRef.current;

    // Reset the previous video
    video.pause();
    video.removeAttribute("src");
    video.load();

    let hls;

    console.log("PLAYING VIDEO:", currentVideo);

    if (
      currentVideo.includes(".m3u8") &&
      Hls.isSupported()
    ) {
      hls = new Hls();

      hls.loadSource(currentVideo);
      hls.attachMedia(video);

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.error("Fatal HLS error:", data);
        }
      });
    } else if (
      currentVideo.includes(".m3u8") &&
      video.canPlayType("application/vnd.apple.mpegurl")
    ) {
      // Native HLS support, mainly Safari
      video.src = currentVideo;
    } else {
      // MP4 or another browser-supported video
      video.src = currentVideo;
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [currentVideo]);

  /* ================================================= */
  /* SELECT LESSON                                     */
  /* ================================================= */

  const selectLesson = (lesson) => {
    console.log("LESSON DATA:", lesson);
    console.log("VIDEO URL:", lesson.video_url);

    if (!lesson.video_url) {
      alert("Video not available");
      return;
    }

    // Reset auto-completion request lock for the new lesson
    completionRequestRef.current = false;

    setCurrentLesson(lesson);
    setCurrentVideo(lesson.video_url);
  };

  /* ================================================= */
  /* MARK LESSON COMPLETE                              */
  /* ================================================= */

  const markLessonComplete = async (lessonId) => {
    if (!lessonId) return;

    // Already completed
    if (completedLessonIds.includes(lessonId)) {
      return;
    }

    // API request already running
    if (
      completionRequestRef.current ||
      markingLessonId === lessonId
    ) {
      return;
    }

    try {
      completionRequestRef.current = true;
      setMarkingLessonId(lessonId);

      await api.post(
        `/students/lessons/${lessonId}/complete`
      );

      setCompletedLessonIds((previousIds) => {
        if (previousIds.includes(lessonId)) {
          return previousIds;
        }

        return [...previousIds, lessonId];
      });

      console.log(
        "Lesson automatically marked as complete:",
        lessonId
      );
    } catch (error) {
      console.error(
        "Mark lesson complete error:",
        error
      );

      completionRequestRef.current = false;

      alert(
        error?.response?.data?.message ||
          "Unable to save lesson completion"
      );
    } finally {
      setMarkingLessonId(null);
    }
  };

  /* ================================================= */
  /* AUTO COMPLETE AT 90%                              */
  /* ================================================= */

  const handleVideoProgress = () => {
    const video = videoRef.current;

    if (!video || !currentLesson?.lesson_id) {
      return;
    }

    const duration = video.duration;
    const currentTime = video.currentTime;

    if (
      !Number.isFinite(duration) ||
      duration <= 0
    ) {
      return;
    }

    const watchedPercentage =
      (currentTime / duration) * 100;

    if (watchedPercentage >= 90) {
      markLessonComplete(
        currentLesson.lesson_id
      );
    }
  };

  /* ================================================= */
  /* HELPER                                            */
  /* ================================================= */

  const isLessonCompleted = (lessonId) =>
    completedLessonIds.includes(lessonId);

  return (
    <div style={{ padding: "20px" }}>
      {/* BACK BUTTON */}

      <div style={{ marginBottom: "15px" }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "#e74c3c",
            color: "#fff",
            border: "none",
            padding: "6px 14px",
            borderRadius: "6px",
            cursor: "pointer"
          }}
        >
          ← Back to Courses
        </button>
      </div>

      {/* MAIN LAYOUT */}

      <div
        style={{
          display: "flex",
          gap: "20px"
        }}
      >
        {/* LEFT SIDE */}

        <div
          style={{
            width: "350px",
            maxHeight: "80vh",
            overflowY: "auto"
          }}
        >
          {modules.map((module, moduleIndex) => (
            <div
              key={module.module_id}
              style={{ marginBottom: "10px" }}
            >
              {/* MODULE HEADER */}

              <div
                onClick={() =>
                  setOpenModule(
                    openModule === module.module_id
                      ? null
                      : module.module_id
                  )
                }
                style={{
                  background: "#3b82a6",
                  color: "#fff",
                  padding: "12px 16px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  display: "flex",
                  justifyContent: "space-between",
                  borderRadius: "6px"
                }}
              >
                <span>
                  {moduleIndex + 1}.{" "}
                  {module.module_title}
                </span>

                <span>
                  {openModule === module.module_id
                    ? "▲"
                    : "▼"}
                </span>
              </div>

              {/* LESSONS */}

              {openModule === module.module_id && (
                <div
                  style={{
                    border: "1px solid #ddd",
                    borderTop: "none",
                    borderRadius: "0 0 6px 6px",
                    background: "#f9f9f9"
                  }}
                >
                  {module.lessons.map((lesson) => {
                    const completed =
                      isLessonCompleted(
                        lesson.lesson_id
                      );

                    const currentlyPlaying =
                      currentLesson?.lesson_id ===
                      lesson.lesson_id;

                    return (
                      <div
                        key={lesson.lesson_id}
                        onClick={() =>
                          selectLesson(lesson)
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "12px",
                          borderBottom:
                            "1px solid #eee",
                          cursor: "pointer",

                          background: currentlyPlaying
                            ? "#e0f2fe"
                            : completed
                            ? "#f0fdf4"
                            : "#fff",

                          borderLeft: currentlyPlaying
                            ? "4px solid #3b82f6"
                            : completed
                            ? "4px solid #16a34a"
                            : "4px solid transparent"
                        }}
                      >
                        {/* PLAY / COMPLETE ICON */}

                        <div
                          style={{
                            width: "50px",
                            height: "50px",
                            minWidth: "50px",
                            background: completed
                              ? "#16a34a"
                              : "#111",
                            color: "#fff",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: "15px",
                            fontSize: completed
                              ? "22px"
                              : "16px",
                            fontWeight: "700"
                          }}
                        >
                          {completed ? "✓" : "▶"}
                        </div>

                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: "500",
                              color: completed
                                ? "#166534"
                                : "#111"
                            }}
                          >
                            {lesson.lesson_title}
                          </div>

                          {/* COMPLETED BADGE */}

                          {completed && (
                            <div
                              style={{
                                display:
                                  "inline-block",
                                marginTop: "4px",
                                fontSize: "11px",
                                padding: "3px 8px",
                                borderRadius: "20px",
                                background: "#dcfce7",
                                color: "#15803d",
                                fontWeight: "600"
                              }}
                            >
                              ✓ Completed
                            </div>
                          )}

                          {/* NOW PLAYING */}

                          {currentlyPlaying &&
                            !completed && (
                              <div
                                style={{
                                  display:
                                    "inline-block",
                                  marginTop: "4px",
                                  fontSize: "11px",
                                  padding: "3px 8px",
                                  borderRadius:
                                    "20px",
                                  background:
                                    "#dbeafe",
                                  color: "#1d4ed8",
                                  fontWeight: "500"
                                }}
                              >
                                ▶ Now Playing
                              </div>
                            )}

                          {/* DOWNLOAD BUTTON */}

                          {lesson.document_url &&
                            lesson.allow_download && (
                              <div>
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();

                                    window.open(
                                      new URL(lesson.document_url, API_ORIGIN).toString(),
                                      "_blank"
                                    );
                                  }}
                                  style={{
                                    marginTop: "5px",
                                    fontSize: "12px",
                                    padding: "4px 8px",
                                    borderRadius:
                                      "6px",
                                    border: "none",
                                    background:
                                      "#eef2ff",
                                    color: "#3b82f6",
                                    cursor: "pointer"
                                  }}
                                >
                                  📄 Download
                                </button>
                              </div>
                            )}

                          <div
                            style={{
                              fontSize: "12px",
                              color: "#777",
                              marginTop: "4px"
                            }}
                          >
                            {module.module_title}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* RIGHT SIDE */}

        <div style={{ flex: 1 }}>
          {/* VIDEO */}

          <div style={{ marginBottom: "20px" }}>
            {currentVideo ? (
              <>
                <video
                  ref={videoRef}
                  controls
                  autoPlay
                  onTimeUpdate={
                    handleVideoProgress
                  }
                  onEnded={() => {
                    if (
                      currentLesson?.lesson_id
                    ) {
                      markLessonComplete(
                        currentLesson.lesson_id
                      );
                    }
                  }}
                  style={{
                    width: "100%",
                    height: "700px",
                    borderRadius: "12px",
                    background: "#000",
                    objectFit: "contain"
                  }}
                />

                {/* CURRENT LESSON STATUS */}

                {currentLesson && (
                  <div
                    style={{
                      marginTop: "12px",
                      padding: "14px 16px",
                      borderRadius: "8px",
                      background: isLessonCompleted(
                        currentLesson.lesson_id
                      )
                        ? "#f0fdf4"
                        : "#eff6ff",
                      border: isLessonCompleted(
                        currentLesson.lesson_id
                      )
                        ? "1px solid #86efac"
                        : "1px solid #bfdbfe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "space-between",
                      gap: "15px"
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#1e293b"
                        }}
                      >
                        {currentLesson.lesson_title}
                      </div>

                      <div
                        style={{
                          marginTop: "4px",
                          fontSize: "13px",
                          color: isLessonCompleted(
                            currentLesson.lesson_id
                          )
                            ? "#15803d"
                            : "#475569"
                        }}
                      >
                        {isLessonCompleted(
                          currentLesson.lesson_id
                        )
                          ? "✓ Lesson completed"
                          : "This lesson will be marked complete automatically after you watch 90%."}
                      </div>
                    </div>

                    {markingLessonId ===
                      currentLesson.lesson_id && (
                      <div
                        style={{
                          color: "#2563eb",
                          fontWeight: "600",
                          fontSize: "13px"
                        }}
                      >
                        Saving progress...
                      </div>
                    )}

                    {isLessonCompleted(
                      currentLesson.lesson_id
                    ) && (
                      <div
                        style={{
                          background: "#16a34a",
                          color: "#fff",
                          padding: "8px 14px",
                          borderRadius: "20px",
                          fontWeight: "600",
                          whiteSpace: "nowrap"
                        }}
                      >
                        ✓ Completed
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "450px",
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #0f172a, #1e293b)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <div
                  style={{
                    background:
                      "rgba(255,255,255,0.05)",
                    backdropFilter: "blur(10px)",
                    padding: "30px 40px",
                    borderRadius: "16px",
                    textAlign: "center",
                    border:
                      "1px solid rgba(255,255,255,0.1)",
                    color: "#fff"
                  }}
                >
                  <img
                    src={logo}
                    alt="MaAz Logo"
                    style={{
                      width: "80px",
                      marginBottom: "10px"
                    }}
                  />

                  <h2
                    style={{
                      marginBottom: "8px"
                    }}
                  >
                    Ready to Learn?
                  </h2>

                  <p
                    style={{
                      color: "#cbd5e1",
                      fontSize: "14px",
                      marginBottom: "18px"
                    }}
                  >
                    Select a lesson from the left
                    to start your journey
                  </p>

                  <button
                    type="button"
                    style={{
                      background: "#3b82f6",
                      color: "#fff",
                      border: "none",
                      padding: "10px 18px",
                      borderRadius: "8px",
                      cursor: "default",
                      fontWeight: "500"
                    }}
                  >
                    Happy Learning 🚀
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* RESOURCES */}

          {currentLesson?.document_url &&
            currentLesson?.allow_download && (
              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "10px",
                  padding: "15px",
                  background: "#f8fafc",
                  boxShadow:
                    "0 2px 8px rgba(0,0,0,0.05)"
                }}
              >
                <h3>📎 Resources</h3>

                <button
                  onClick={() =>
                    window.open(
                      new URL(currentLesson.document_url, API_ORIGIN).toString(),
                      "_blank"
                    )
                  }
                  style={{
                    marginTop: "10px",
                    padding: "8px 14px",
                    borderRadius: "6px",
                    border: "none",
                    background: "#3b82f6",
                    color: "#fff",
                    cursor: "pointer"
                  }}
                >
                  Download Notes
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default CoursePlayer;